/**
 * Typed GraphQL reads for places (place manager, unit 0). Read-only: nothing in
 * this module writes.
 *
 * A place is a brand's persistent home — its main room plus the tables open
 * inside it. A brand owns one or more (one of them its default); house places have no brand.
 * Field names follow the schema snapshot (`reloaded-rest/schema.graphql`):
 * rooms carry a `title` (not `name`), and a room's current
 * activity is `Gathering.activity` (its slug keys it, its name is shown), the people in it
 * `Gathering.gatheringUsers` (the first 6 users: `FACES_SHOWN` in
 * `place-today.tsx`, tied by a test), and its rules preset
 * `Gathering.settings` (public, no staff door needed).
 */

import { graphql, gqlRequest, gqlRequestAs, type RequestOptions, type ResultOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

export const PlaceFields = graphql(`
  fragment PlaceFields on Place {
    id
    name
    isDefault
    brand {
      id
      name
    }
    rootGathering {
      id
      title
      onlineUsersCount
      totalVisitorCount
      activity {
        id
        slug
        name
      }
      gatheringUsers(first: 6) {
        totalCount
        nodes {
          id
          public {
            displayName
            username
            photoURL
          }
        }
      }
      settings {
        id
        name
        slug
      }
    }
    subGatherings {
      totalCount
      nodes {
        id
        title
        onlineUsersCount
        activity {
          id
          slug
          name
        }
        gatheringUsers(first: 6) {
          totalCount
          nodes {
            id
            public {
              displayName
              username
              photoURL
            }
          }
        }
        settings {
          id
          name
          slug
        }
      }
    }
  }
`);

/** A place as the portal reads it. */
export type PlaceData = ResultOf<typeof PlaceFields>;

/**
 * A place as a row in the list: just what the row shows. The headcount needs
 * every open table's count (the backend sends 50 tables at most; past that the
 * row shows a floor), but not their titles or games — those stay on the
 * detail read ({@link PlaceFields}). A place's seasons are their own read
 * (`PlaceSeasonsQuery` in `place-events-api.ts`), once per visit, not polled.
 */
export const PlaceListFields = graphql(`
  fragment PlaceListFields on Place {
    id
    name
    isDefault
    brand {
      id
      name
    }
    rootGathering {
      id
      onlineUsersCount
    }
    subGatherings {
      totalCount
      nodes {
        onlineUsersCount
      }
    }
  }
`);

/** A place as the list reads it. */
export type PlaceListData = ResultOf<typeof PlaceListFields>;

/** Rows per page of the staff list (the backend's own default and cap). */
const PLACES_PAGE = 100;

const AdminAllPlacesQuery = graphql(
  `
  query AdminAllPlaces($first: Int!, $offset: Int!) {
    admin_allPlaces(first: $first, offset: $offset) {
      ...PlaceListFields
    }
  }
`,
  [PlaceListFields],
);

/** Every place, brand-owned or house, newest first (STAFF ONLY — goes
 *  through the staff door, so the viewer must be a super admin). The query
 *  returns a bare list with no total, so it pages 100 at a time until a short
 *  page: a silent cut at 100 would hide the oldest places. */
export async function fetchAllPlaces(viewer: RequestViewer): Promise<PlaceListData[]> {
  const places: PlaceListData[] = [];
  for (let offset = 0; ; offset += PLACES_PAGE) {
    const data = await gqlRequestAs(viewer, AdminAllPlacesQuery, { first: PLACES_PAGE, offset });
    places.push(...data.admin_allPlaces);
    if (data.admin_allPlaces.length < PLACES_PAGE) return places;
  }
}

const BrandPlacesQuery = graphql(
  `
  query BrandPlaces($id: ID!) {
    brand(id: $id) {
      id
      places {
        ...PlaceListFields
      }
    }
  }
`,
  [PlaceListFields],
);

/** Every place a brand owns, oldest first, its default place marked
 *  `isDefault`. Empty while a just-created brand is still being seeded, or when
 *  the caller cannot see the brand. */
export async function fetchBrandPlaces(brandId: string): Promise<PlaceListData[]> {
  const data = await gqlRequest(BrandPlacesQuery, { id: brandId });
  return data.brand?.places ?? [];
}

const PlaceQuery = graphql(
  `
  query Place($id: ID!) {
    place(id: $id) {
      ...PlaceFields
    }
  }
`,
  [PlaceFields],
);

/** A single place by public id. Null when it does not exist or the caller
 *  cannot see it (a DRAFT brand's place, to a non-member). `options.signal`
 *  aborts the request (the polled re-read times out this way). */
export async function fetchPlace(id: string, viewer: RequestViewer, options?: RequestOptions): Promise<PlaceData | null> {
  const data = await gqlRequestAs(viewer, PlaceQuery, { id }, options);
  return data.place;
}
